import { AnimatePresence, motion } from 'framer-motion'
import { lazy, Suspense, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { Check } from '@phosphor-icons/react/dist/ssr/Check'
import { CopySimple } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { ArrowUpRight } from '@phosphor-icons/react/dist/ssr'
import essayIcon from '@/assets/icons/essay.svg'
import authorPortrait from '@/assets/portrait-gosha-front-80.avif'
import portraitFront from '@/assets/portrait-gosha-front.avif'
import { EditorialQuote } from '@/components/article/EditorialQuote'
import { ArticleProse, renderRichText } from '@/components/article/ArticleProse'
import { CaseStudyBookCallCta } from '@/components/case-study/CaseStudyBookCallCta'
import { CaseStudyContentSheet } from '@/components/case-study/CaseStudyContentSheet'
import { CaseStudyFaqAccordion } from '@/components/case-study/CaseStudyFaqAccordion'
import type { TocItem } from '@/components/case-study/caseStudyDefaultToc'
import { CaseStudyHeroBadges } from '@/components/case-study/CaseStudyHeroBadges'
import { HeroAsideLabelTip } from '@/components/case-study/HeroAsideLabelTip'
import { HomeAiCaseStudies } from '@/components/home/HomeAiCaseStudies'
import { SeoHead } from '@/components/seo/SeoHead'
import { siteConfig } from '@/config/site'
import { getAllCaseStudies, getAllNotes } from '@/data/contentService'
import type { CaseStudy, Note } from '@/domain/content'
import { resolveCaseStudyClientLogo } from '@/lib/caseStudyClientLogo'
import { caseStudyBlogPath } from '@/lib/caseStudyPublicUrl'
import { getCaseStudySlug } from '@/lib/caseStudySlug'
import { resolveSyncedCaseStudyTags } from '@/lib/caseStudyTags'
import { copyContactEmail } from '@/lib/copyContactEmail'

const MermaidBlock = lazy(() =>
  import('@/components/case-study/MermaidBlock').then((m) => ({ default: m.MermaidBlock }))
)

type Props = {
  post: Note
  pathname: string
}

const slugifyHeading = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')

const extractNoteTocItems = (content: string): TocItem[] =>
  content.split('\n').reduce<TocItem[]>((items, line) => {
    const h2 = line.trim().match(/^##\s+(.+)$/)
    if (h2) {
      items.push({ label: h2[1], anchor: `#${slugifyHeading(h2[1])}`, depth: 0 })
      return items
    }
    const h3 = line.trim().match(/^###\s+(.+)$/)
    if (h3) {
      items.push({ label: h3[1], anchor: `#${slugifyHeading(h3[1])}`, depth: 1 })
    }
    return items
  }, [])

const getNoteTocItems = (post: Note): TocItem[] => {
  const items = extractNoteTocItems(post.content)
  const firstBodySectionIndex = items.findIndex((item) => item.depth === 0 && item.label.toLowerCase() !== 'tl;dr')
  if (post.quote?.body?.trim()) {
    items.push({ label: 'Quote', anchor: '#quote', depth: 0 })
  }
  if (post.workflow_schema?.body?.trim()) {
    const workflowItem = {
      label: post.workflow_schema.heading ?? 'Workflow schema',
      anchor: '#workflow-schema',
      depth: 0,
    }
    const afterHeading = post.workflow_schema.position?.after_heading?.replace(/^#/, '').trim()
    const afterHeadingSlug = afterHeading ? slugifyHeading(afterHeading) : ''
    const insertAfterIndex = afterHeadingSlug
      ? items.findIndex((item) => item.anchor.replace(/^#/, '') === afterHeadingSlug)
      : -1
    if (insertAfterIndex >= 0) {
      items.splice(insertAfterIndex + 1, 0, workflowItem)
    } else if (firstBodySectionIndex >= 0) {
      items.splice(firstBodySectionIndex + 1, 0, workflowItem)
    } else {
      items.push(workflowItem)
    }
  }
  if (post.faq?.items?.length) {
    items.push({ label: post.faq.heading ?? 'FAQ', anchor: '#faq', depth: 0 })
  }
  items.push({ label: 'Author', anchor: '#author', depth: 0 })
  return items
}

const relatedCaseStudyClient = (caseStudy: CaseStudy) => caseStudy.hero?.client?.trim() || 'Case study'

const RelatedCaseStudyBlock = ({ caseStudy }: { caseStudy: CaseStudy }) => {
  const client = relatedCaseStudyClient(caseStudy)
  const logo = resolveCaseStudyClientLogo(client)
  const tags = resolveSyncedCaseStudyTags({
    primary: caseStudy.hero?.tags,
    fallback: caseStudy.tags,
    max: 4,
    reverse: false,
  })

  return (
    <section id="related-case-study" className="not-prose my-8 scroll-mt-28 md:scroll-mt-20">
      <a
        href={caseStudyBlogPath(caseStudy.page.slug)}
        className="group flex w-full flex-col items-start gap-4 overflow-hidden rounded-[16px] bg-black px-4 py-3 text-white !no-underline transition-[transform,box-shadow] duration-200 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:shadow-[0px_1px_1px_rgba(0,0,0,0.08),0px_18px_34px_rgba(0,0,0,0.12)] active:scale-[0.998] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
      >
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full items-end justify-between gap-3">
            <div className="flex min-w-0 items-end gap-2">
              <span className="relative size-6 shrink-0 overflow-hidden rounded-[4px]">
                {logo ? (
                  <img src={logo} alt={`${client} logo`} className="!my-0 size-full !rounded-[4px] !border-0 object-contain" width={24} height={24} />
                ) : (
                  <span className="inline-flex size-full items-center justify-center bg-white text-[10px] font-semibold text-black">
                    {client.slice(0, 2).toUpperCase()}
                  </span>
                )}
              </span>
              <span className="min-w-0 truncate text-[15px] leading-[22.5px] tracking-[-0.16px] text-white">{client}</span>
            </div>
            <span className="shrink-0 font-display text-[15px] font-normal leading-[22.5px] tracking-[-0.16px] text-white/70 underline decoration-white/45 underline-offset-[3px] [transition-property:color,text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] group-hover:text-white group-hover:decoration-white">
              Read More
            </span>
          </div>
          <div className="font-display text-[20px] font-normal leading-6 tracking-[-0.2px] text-white [text-wrap:pretty]">
            {caseStudy.page.title}
          </div>
        </div>
        {tags.length ? (
          <div className="flex flex-wrap items-end gap-x-1 gap-y-1 pt-5">
            {tags.map((tag, index) => (
              <span
                key={`${caseStudy.page.slug}-${tag}-${index}`}
                className="inline-flex h-[26px] shrink-0 items-center justify-center rounded-full border border-[#d6d6d6] bg-white px-2.5 text-center text-[12px] leading-3 text-black whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}
      </a>
    </section>
  )
}

const renderMarkdown = (
  content: string,
  options?: {
    relatedCaseStudyBlock?: ReactNode
    relatedCaseStudyAfterHeading?: string
    relatedCaseStudyAfterParagraph?: string
    workflowSchemaAfterHeading?: string
    workflowSchemaAfterParagraph?: string
    workflowSchemaBlock?: ReactNode
  },
) => {
  const blocks: ReactElement[] = []
  const lines = content.trim().split('\n')
  let paragraph: string[] = []
  let list: string[] = []
  let listType: 'ul' | 'ol' | null = null
  let insertedRelatedCaseStudy = false
  let pendingRelatedCaseStudy = false
  let insertedWorkflowSchema = false
  let pendingWorkflowSchema = false
  let currentH2: string | null = null

  const normalizePositionText = (value: string) => value.replace(/\s+/g, ' ').trim()

  const maybeInsertWorkflowSchema = () => {
    if (!options?.workflowSchemaBlock || insertedWorkflowSchema) {
      return
    }
    if (options.workflowSchemaAfterHeading?.trim() || options.workflowSchemaAfterParagraph?.trim()) {
      return
    }
    if (!currentH2 || currentH2.toLowerCase() === 'tl;dr') {
      return
    }
    blocks.push(<div key={`workflow-schema-${blocks.length}`} className="not-prose">{options.workflowSchemaBlock}</div>)
    insertedWorkflowSchema = true
  }

  const queueWorkflowSchemaAfterHeading = (heading: string) => {
    if (!options?.workflowSchemaBlock || insertedWorkflowSchema) {
      return
    }
    if (options.workflowSchemaAfterParagraph?.trim()) {
      return
    }
    const target = options.workflowSchemaAfterHeading?.replace(/^#/, '').trim()
    const targetSlug = target ? slugifyHeading(target) : ''
    if (targetSlug && slugifyHeading(heading) !== targetSlug) {
      return
    }
    pendingWorkflowSchema = true
  }

  const flushPendingWorkflowSchema = () => {
    if (!pendingWorkflowSchema || !options?.workflowSchemaBlock || insertedWorkflowSchema) {
      return
    }
    blocks.push(<div key={`workflow-schema-${blocks.length}`} className="not-prose">{options.workflowSchemaBlock}</div>)
    insertedWorkflowSchema = true
    pendingWorkflowSchema = false
  }

  const queueRelatedCaseStudyAfterHeading = (heading: string) => {
    if (!options?.relatedCaseStudyBlock || insertedRelatedCaseStudy) {
      return
    }
    if (options.relatedCaseStudyAfterParagraph?.trim()) {
      return
    }
    const target = options.relatedCaseStudyAfterHeading?.replace(/^#/, '').trim()
    const targetSlug = target ? slugifyHeading(target) : ''
    if (targetSlug && slugifyHeading(heading) !== targetSlug) {
      return
    }
    pendingRelatedCaseStudy = true
  }

  const flushPendingRelatedCaseStudy = () => {
    if (!pendingRelatedCaseStudy || !options?.relatedCaseStudyBlock || insertedRelatedCaseStudy) {
      return
    }
    blocks.push(<div key={`related-case-study-${blocks.length}`} className="not-prose">{options.relatedCaseStudyBlock}</div>)
    insertedRelatedCaseStudy = true
    pendingRelatedCaseStudy = false
  }

  const flushParagraph = () => {
    if (paragraph.length === 0) return
    const text = paragraph.join(' ')
    blocks.push(<p key={`p-${blocks.length}`}>{renderRichText(text)}</p>)
    paragraph = []
    const afterParagraph = options?.relatedCaseStudyAfterParagraph
    if (
      afterParagraph &&
      options?.relatedCaseStudyBlock &&
      !insertedRelatedCaseStudy &&
      normalizePositionText(text) === normalizePositionText(afterParagraph)
    ) {
      blocks.push(<div key={`related-case-study-${blocks.length}`} className="not-prose">{options.relatedCaseStudyBlock}</div>)
      insertedRelatedCaseStudy = true
      pendingRelatedCaseStudy = false
    }
    const workflowAfterParagraph = options?.workflowSchemaAfterParagraph
    if (
      workflowAfterParagraph &&
      options?.workflowSchemaBlock &&
      !insertedWorkflowSchema &&
      normalizePositionText(text) === normalizePositionText(workflowAfterParagraph)
    ) {
      blocks.push(<div key={`workflow-schema-${blocks.length}`} className="not-prose">{options.workflowSchemaBlock}</div>)
      insertedWorkflowSchema = true
      pendingWorkflowSchema = false
    }
    flushPendingRelatedCaseStudy()
    flushPendingWorkflowSchema()
  }

  const flushList = () => {
    if (list.length === 0) return
    const ListTag = listType ?? 'ul'
    blocks.push(
      <ListTag key={`${ListTag}-${blocks.length}`}>
        {list.map((item, index) => (
          <li key={`${item}-${index}`}>{renderRichText(item)}</li>
        ))}
      </ListTag>,
    )
    list = []
    listType = null
  }

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) {
      flushParagraph()
      flushList()
      continue
    }

    if (trimmed.startsWith('## ')) {
      flushParagraph()
      flushList()
      maybeInsertWorkflowSchema()
      const title = trimmed.slice(3)
      blocks.push(
        <h2 id={slugifyHeading(title)} key={`h2-${blocks.length}`} className="scroll-mt-28 md:scroll-mt-20">
          {title}
        </h2>,
      )
      currentH2 = title
      queueRelatedCaseStudyAfterHeading(title)
      queueWorkflowSchemaAfterHeading(title)
      continue
    }

    if (trimmed.startsWith('### ')) {
      flushParagraph()
      flushList()
      const title = trimmed.slice(4)
      blocks.push(
        <h3 id={slugifyHeading(title)} key={`h3-${blocks.length}`} className="scroll-mt-28 md:scroll-mt-20">
          {title}
        </h3>,
      )
      queueRelatedCaseStudyAfterHeading(title)
      queueWorkflowSchemaAfterHeading(title)
      continue
    }

    if (trimmed.startsWith('- ')) {
      flushParagraph()
      if (listType && listType !== 'ul') {
        flushList()
      }
      listType = 'ul'
      list.push(trimmed.slice(2))
      continue
    }

    const orderedListMatch = trimmed.match(/^\d+\.\s+(.+)$/)
    if (orderedListMatch) {
      flushParagraph()
      if (listType && listType !== 'ol') {
        flushList()
      }
      listType = 'ol'
      list.push(orderedListMatch[1])
      continue
    }

    flushList()
    paragraph.push(trimmed)
  }

  flushParagraph()
  flushList()
  maybeInsertWorkflowSchema()

  if (options?.relatedCaseStudyBlock && !insertedRelatedCaseStudy) {
    blocks.push(<div key={`related-case-study-${blocks.length}`}>{options.relatedCaseStudyBlock}</div>)
  }

  return blocks
}

const statLabelClass = 'text-[11px] font-semibold uppercase leading-[16.5px] tracking-[0.275px] text-[#636363]'
const statValueClass = 'text-sm font-normal leading-[19.25px] text-[var(--foreground)]'
const statStrongValueClass = 'text-right text-sm font-bold leading-[19.25px] text-[var(--foreground)]'
const contributorsTooltip =
  'Number of domain experts the author discussed this idea with in live conversation before publishing.'
const NOTE_AUTHOR_BIO =
  'I build AI-native workflows that turn context into useful sales, content, and publishing artifacts before work becomes manual production.'
const NOTE_DATE_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatNotePublishDate = (date: string) => {
  const parts = date.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!parts) {
    return date
  }
  const month = NOTE_DATE_MONTHS[Number(parts[2]) - 1]
  if (!month) {
    return date
  }
  return `${Number(parts[3])} ${month}, ${parts[1]}`
}

const NoteMainIdeaRow = ({ value }: { value: string }) => (
  <div className="py-3">
    <div className="flex flex-col gap-1">
      <div className={statLabelClass}>Main idea</div>
      <div className={`${statValueClass} [text-wrap:pretty]`}>{value}</div>
    </div>
  </div>
)

const NoteStatRow = ({ label, value, explanation }: { label: string; value: string; explanation?: string }) => (
  <div className="flex min-h-11 items-center justify-between gap-2 border-b border-[#e0e0e0] py-3">
    <div className="flex max-w-[min(104px,45%)] min-w-0 shrink-0 items-center overflow-visible">
      <div className={`inline-flex max-w-full min-w-0 flex-nowrap items-center gap-0.5 whitespace-nowrap ${statLabelClass}`}>
        {label}
        {explanation ? <HeroAsideLabelTip label={label} explanation={explanation} /> : null}
      </div>
    </div>
    <div className={`${statStrongValueClass} min-w-0 [overflow-wrap:anywhere]`}>{value}</div>
  </div>
)

const NoteAuthorRow = () => (
  <div className="flex min-h-11 items-center justify-between gap-2 border-b border-[#e0e0e0] py-3">
    <div className="flex max-w-[min(104px,45%)] min-w-0 shrink-0 items-center overflow-visible">
      <div className={statLabelClass}>Author</div>
    </div>
    <div className="flex min-w-0 items-center justify-end gap-2">
      <img
        src={authorPortrait}
        alt=""
        className="h-6 w-4 shrink-0 rounded-[4px] object-cover"
        width={16}
        height={24}
        loading="lazy"
        decoding="async"
        aria-hidden
      />
      <div className={`${statStrongValueClass} min-w-0 [overflow-wrap:anywhere]`}>{siteConfig.authorName}</div>
    </div>
  </div>
)

const NoteStats = ({ post }: { post: Note }) => (
  <aside className="mx-auto flex w-full min-w-0 max-w-[448px] shrink flex-col gap-8 lg:mx-0 lg:w-[252px] lg:max-w-[252px] lg:self-start">
    <div className="flex flex-col">
      <NoteMainIdeaRow value={post.excerpt} />
    </div>
    <div className="flex flex-col">
      <NoteStatRow label="Audience" value={post.audience ?? '—'} />
      <NoteStatRow label="Contributors" value={post.contributors?.toString() ?? '—'} explanation={contributorsTooltip} />
      <NoteStatRow label="Publish date" value={formatNotePublishDate(post.date)} />
      <NoteAuthorRow />
    </div>
  </aside>
)

type NoteSectionProps = {
  id: string
  title?: string
  children: ReactNode
}

const NoteSection = ({ id, title, children }: NoteSectionProps) => (
  <section id={id} className="scroll-mt-28 md:scroll-mt-20">
    {title ? (
      <h2 className="ui-heading-balance mb-4 font-display text-[20px] font-normal leading-6 tracking-[-0.2px] text-[var(--foreground)]">
        {title}
      </h2>
    ) : null}
    {children}
  </section>
)

const NoteWorkflowSchemaBlock = ({ workflowSchema }: { workflowSchema: NonNullable<Note['workflow_schema']> }) => (
  <section id="workflow-schema" className="not-prose my-8 scroll-mt-28 md:scroll-mt-20">
    <h2 className="ui-heading-balance mb-4 font-display text-[20px] font-normal leading-6 tracking-[-0.2px] text-[var(--foreground)]">
      {workflowSchema.heading ?? 'Workflow schema'}
    </h2>
    {workflowSchema.type === 'mermaid' ||
    /flowchart|graph\s|sequenceDiagram|classDiagram/i.test(workflowSchema.body) ? (
      <Suspense fallback={null}>
        <MermaidBlock
          chart={workflowSchema.body.replace(/^```mermaid\n?|\n?```$/g, '').trim()}
        />
      </Suspense>
    ) : (
      <pre className="overflow-x-auto rounded-[16px] border border-[#e0e0e0] bg-[#fafafa] p-4 font-mono text-sm leading-6 text-[var(--foreground)]">
        {workflowSchema.body}
      </pre>
    )}
  </section>
)

export const NotePage = ({ post, pathname }: Props) => {
  const [aboutEmailCopied, setAboutEmailCopied] = useState(false)
  const [copyEmailToastVisible, setCopyEmailToastVisible] = useState(false)
  const aboutCopyTimerRef = useRef<number | null>(null)
  const copyToastResetTimerRef = useRef<number | null>(null)
  const allCaseStudies = getAllCaseStudies()
  const allNotes = getAllNotes()
  const tocItems = getNoteTocItems(post)
  const readAlsoCaseStudies = allCaseStudies.slice(0, 4)
  const readAlsoNotes = allNotes.filter((note) => note.slug !== post.slug).slice(0, 2)
  const relatedCaseStudy = post.related_case_study?.slug
    ? allCaseStudies.find((caseStudy) => getCaseStudySlug(caseStudy) === post.related_case_study?.slug)
    : undefined
  const relatedCaseStudyBlock = relatedCaseStudy ? (
    <RelatedCaseStudyBlock
      caseStudy={relatedCaseStudy}
    />
  ) : undefined
  const quote = post.quote?.body?.trim() ? post.quote : undefined
  const workflowSchema = post.workflow_schema?.body?.trim() ? post.workflow_schema : undefined
  const workflowSchemaBlock = workflowSchema ? <NoteWorkflowSchemaBlock workflowSchema={workflowSchema} /> : undefined
  const faq = post.faq?.items?.length ? post.faq : undefined

  useEffect(
    () => () => {
      if (aboutCopyTimerRef.current !== null) {
        window.clearTimeout(aboutCopyTimerRef.current)
      }
      if (copyToastResetTimerRef.current !== null) {
        window.clearTimeout(copyToastResetTimerRef.current)
      }
    },
    [],
  )

  const showCopyEmailToast = () => {
    setCopyEmailToastVisible(true)
    if (copyToastResetTimerRef.current !== null) {
      window.clearTimeout(copyToastResetTimerRef.current)
    }
    copyToastResetTimerRef.current = window.setTimeout(() => {
      setCopyEmailToastVisible(false)
      copyToastResetTimerRef.current = null
    }, 1800)
  }

  const handleAboutCopyEmail = async () => {
    const ok = await copyContactEmail(siteConfig.contactEmail)
    if (!ok) {
      return
    }
    setAboutEmailCopied(true)
    if (aboutCopyTimerRef.current !== null) {
      window.clearTimeout(aboutCopyTimerRef.current)
    }
    aboutCopyTimerRef.current = window.setTimeout(() => {
      setAboutEmailCopied(false)
      aboutCopyTimerRef.current = null
    }, 1800)
    showCopyEmailToast()
  }

  return (
    <>
      <SeoHead pathname={pathname} kind="post" post={post} />
      <div className="site-page mx-auto w-full min-w-0 max-w-[1104px] px-4 pb-32 md:px-6">
        <div className="grid w-full grid-cols-1 gap-x-10 gap-y-6 lg:mx-auto lg:w-fit lg:grid-cols-[minmax(0,252px)_minmax(0,448px)_minmax(0,240px)] lg:gap-x-6 xl:gap-x-10">
          <header className="space-y-4 lg:col-start-2 lg:row-start-1">
            <div className="mx-auto w-full min-w-0 max-w-[448px] lg:mx-0">
              <div aria-label="Essay">
                <span className="relative z-10 flex flex-row items-center py-2.5">
                  <img src={essayIcon} alt="" className="h-5 w-auto shrink-0" aria-hidden />
                </span>
              </div>
              <h1 className="ui-heading-balance font-display text-[28px] font-semibold leading-[1.06] tracking-[-0.56px] text-[var(--foreground)] [text-wrap:pretty]">
                {post.title}
              </h1>
              <div className="mt-4">
                <CaseStudyHeroBadges
                  tags={post.tags}
                  readEstimate={post.readTime ? `${post.readTime} min read` : undefined}
                />
              </div>
            </div>
          </header>

          <div className="lg:col-start-1 lg:row-start-2">
            <NoteStats post={post} />
          </div>

          <article className="mx-auto w-full min-w-0 max-w-[448px] shrink lg:col-start-2 lg:row-start-2 lg:mx-0">
            <div className="flex flex-col gap-14">
              <section>
                <ArticleProse>
                  <div className="[&>h2:first-child]:!mt-0">
                    {renderMarkdown(post.content, {
                      relatedCaseStudyBlock,
                      relatedCaseStudyAfterHeading: post.related_case_study?.position?.after_heading,
                      relatedCaseStudyAfterParagraph: post.related_case_study?.position?.after_paragraph,
                      workflowSchemaAfterHeading: post.workflow_schema?.position?.after_heading,
                      workflowSchemaAfterParagraph: post.workflow_schema?.position?.after_paragraph,
                      workflowSchemaBlock,
                    })}
                  </div>
                </ArticleProse>
                {post.sourceUrl ? (
                  <a
                    href={post.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-10 inline-flex items-center gap-1.5 text-[14px] font-medium text-[var(--foreground)] underline underline-offset-2"
                  >
                    Source
                    <ArrowUpRight size={15} weight="regular" aria-hidden />
                  </a>
                ) : null}
              </section>

              {quote ? (
                <NoteSection id="quote">
                  <EditorialQuote
                    body={quote.body}
                    attribution={quote.attribution}
                    name={quote.name}
                    role={quote.role}
                    company={quote.company}
                    photoUrl={quote.photo_url}
                    photoAlt={quote.photo_alt}
                  />
                </NoteSection>
              ) : null}

              {faq ? (
                <NoteSection id="faq" title={faq.heading ?? 'FAQ'}>
                  <CaseStudyFaqAccordion items={faq.items} />
                </NoteSection>
              ) : null}

              <section id="author" className="scroll-mt-28 md:scroll-mt-20">
                <div className="flex min-w-0 flex-col gap-5">
                  <div className="flex items-start justify-between gap-3 sm:gap-4">
                    <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
                      <div className="relative shrink-0">
                        <div className="overflow-hidden rounded-[5px] [box-shadow:0_1px_2px_rgba(0,0,0,0.05)]">
                          <img
                            src={portraitFront}
                            alt={`Portrait of ${siteConfig.authorName}`}
                            className="h-[42px] w-7 object-cover sm:h-[48px] sm:w-8"
                            width={32}
                            height={48}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <span
                          className="absolute right-0 bottom-0 size-2 translate-x-px translate-y-px rounded-full bg-[#22c55e] ring-2 ring-[var(--background)]"
                          aria-hidden
                        />
                      </div>
                      <div className="min-w-0 space-y-0.5 pt-px">
                        <div className="font-display text-lg font-semibold leading-tight tracking-tight text-[var(--foreground)]">
                          {siteConfig.authorName}
                        </div>
                        <div className="text-sm leading-snug text-[var(--foreground-muted)]">{siteConfig.authorTitle}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleAboutCopyEmail}
                      aria-label="Copy email address"
                      className="group inline-flex shrink-0 items-center gap-1 self-start font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)]"
                    >
                      email
                      <span className="relative inline-flex size-5 items-center justify-center" aria-hidden>
                        <motion.span
                          animate={{
                            opacity: aboutEmailCopied ? 0 : 1,
                            scale: aboutEmailCopied ? 0.25 : 1,
                            filter: aboutEmailCopied ? 'blur(4px)' : 'blur(0px)',
                          }}
                          transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                          className="absolute inset-0 flex items-center justify-center"
                        >
                          <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                            <CopySimple size={16} weight="fill" />
                          </span>
                          <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                            <CopySimple size={16} weight="regular" />
                          </span>
                        </motion.span>
                        <motion.span
                          animate={{
                            opacity: aboutEmailCopied ? 1 : 0,
                            scale: aboutEmailCopied ? 1 : 0.25,
                            filter: aboutEmailCopied ? 'blur(0px)' : 'blur(4px)',
                          }}
                          transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                          className="absolute inset-0 flex items-center justify-center"
                        >
                          <Check size={16} weight="bold" />
                        </motion.span>
                      </span>
                    </button>
                  </div>
                  <p className="text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty]">
                    {NOTE_AUTHOR_BIO}
                  </p>
                </div>
              </section>

              {readAlsoCaseStudies.length ? (
                <HomeAiCaseStudies
                  title="Read Also"
                  items={readAlsoCaseStudies}
                  notes={readAlsoNotes}
                  showExploreMore
                  padded={false}
                  variant="readAlso"
                />
              ) : null}
            </div>
          </article>

          <aside className="hidden min-w-0 lg:col-start-3 lg:row-start-2 lg:block">
            <div className="mx-auto flex w-full min-w-0 max-w-[448px] shrink flex-col gap-8 py-3 lg:sticky lg:top-0 lg:mx-0 lg:max-w-[240px] lg:self-start lg:h-[100dvh] lg:gap-0 lg:overflow-hidden lg:pb-4 lg:pt-0">
              <div className="min-h-0 pt-4 lg:flex-1 lg:overflow-y-auto lg:overscroll-y-contain [scrollbar-width:thin]">
                {tocItems.length ? <CaseStudyContentSheet items={tocItems} /> : null}
              </div>
              <div className="hidden shrink-0 lg:block">
                <CaseStudyBookCallCta variant="inline" />
              </div>
            </div>
          </aside>
        </div>
      </div>
      <AnimatePresence>
        {copyEmailToastVisible ? (
          <motion.div
            key="note-email-copied-toast"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
            className="pointer-events-none fixed inset-x-0 z-[60] flex justify-center px-4 max-md:bottom-[calc(4rem+60px+env(safe-area-inset-bottom,0px))] md:bottom-8"
            role="status"
            aria-live="polite"
          >
            <div className="rounded-[var(--radius-lg)] bg-[var(--primary)] px-3 py-2 text-[13px] font-medium tracking-[-0.13px] text-[var(--primary-foreground)] shadow-[0_8px_24px_rgba(0,0,0,0.28)]">
              Email copied
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )
}
